import { isRecord } from '@atlassian/conversation-assistant-utils/isRecord';

// Referenced from jira/src/packages/platform/utils/fetch/src/utils/is-error.tsx
// Issues we are not gonna track in Sentry
const KNOWN_NETWORK_ERRORS_LIMITED = [
	'server with the specified hostname could not be found',
	'connection failure',
	'Failed to fetch',
	'Load failed', // Safari equivalent of "Failed to fetch"
	'Network request failed',
	'NetworkError',
	'The request timed out',
	'The network connection was lost',
	'The operation was aborted',
];

const isClientFetchErrorMessage = (message: string): boolean => {
	const errorTypes = KNOWN_NETWORK_ERRORS_LIMITED;
	return (
		Boolean(message) &&
		!!errorTypes.find((networkErrorMessage) =>
			// Using `includes` here because the underlying fetch error messages
			// are sometimes wrapped to provide additional context, e.g.
			// "Something went wrong: ${error.message}"
			message.includes(networkErrorMessage),
		)
	);
};

const KNOWN_NETWORK_ERRORS_TYPES = ['AbortError', 'AuthError', 'ChunkLoadError', 'NetworkError'];

const MAX_ERROR_CAUSE_DEPTH = 3;

const getErrorAndCauseRecords = (error: unknown): Record<string, unknown>[] => {
	const records: Record<string, unknown>[] = [];
	let currentError = error;

	for (let depth = 0; depth < MAX_ERROR_CAUSE_DEPTH && isRecord(currentError); depth++) {
		records.push(currentError);
		currentError = currentError.cause;
	}

	return records;
};

const getErrorStatusCode = (error: Record<string, unknown>): number | undefined => {
	const errorAttributes = isRecord(error.errorAttributes) ? error.errorAttributes : undefined;
	const attributes = isRecord(error.attributes) ? error.attributes : undefined;
	const status = errorAttributes?.statusCode ?? error.status ?? attributes?.status;
	return typeof status === 'number' ? status : undefined;
};

const getErrorCode = (error: Record<string, unknown>): string | undefined => {
	const errorAttributes = isRecord(error.errorAttributes) ? error.errorAttributes : undefined;
	return typeof errorAttributes?.errorCode === 'string'
		? errorAttributes.errorCode.toLowerCase()
		: undefined;
};

export const isClientFetchError = (error: unknown): boolean => {
	if (!isRecord(error)) {
		return false;
	}

	// Check top-level error name and message
	const hasKnownName =
		typeof error.name === 'string' && KNOWN_NETWORK_ERRORS_TYPES.includes(error.name);
	const hasKnownMessage =
		typeof error.message === 'string' && isClientFetchErrorMessage(error.message);
	if (hasKnownName || hasKnownMessage) {
		return true;
	}

	// Also check the nested cause (e.g. wrapped fetch errors)
	if (!isRecord(error.cause)) {
		return false;
	}
	return (
		(typeof error.cause.name === 'string' &&
			KNOWN_NETWORK_ERRORS_TYPES.includes(error.cause.name)) ||
		(typeof error.cause.message === 'string' && isClientFetchErrorMessage(error.cause.message))
	);
};

/**
 * Determines if an error is an auth or permission error (401/403).
 * These errors are caused by the user not being authenticated or not having
 * permission to use the feature (e.g. IP allowlist, HIPAA, product not licensed).
 * They should be excluded from SLO failure counts as they are not service reliability issues.
 */
export const isAuthOrPermissionError = (error: unknown): boolean => {
	return getErrorAndCauseRecords(error).some((errorRecord) => {
		const status = getErrorStatusCode(errorRecord);
		return status === 401 || status === 403;
	});
};

/**
 * Determines if an error is a rate-limit response (HTTP 429).
 * Rate limits should be excluded from the main reliability SLO because they
 * represent admission control rather than a service failure, but are still
 * tracked separately through the filtered-client-error experience.
 */
export const isRateLimitError = (error: unknown): boolean => {
	return getErrorAndCauseRecords(error).some(
		(errorRecord) => getErrorStatusCode(errorRecord) === 429,
	);
};

/**
 * Determines if an error is a "product not active" or "access denied" error.
 * These occur when Rovo is not enabled for the user's org/product, predominantly
 * in Jira. They should be excluded from SLO failure counts.
 *
 * Uses the structured errorCode from ApiError.errorAttributes rather than
 * string matching on the human-readable message field.
 */
export const isProductNotActiveError = (error: unknown): boolean => {
	return getErrorAndCauseRecords(error).some((errorRecord) => {
		const errorCode = getErrorCode(errorRecord) ?? '';
		return errorCode.includes('product.not.active') || errorCode.includes('product_not_active');
	});
};
